 
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { CATALOGUE_ENABLED } from '../config/features';
import { 
  Target, 
  Heart, 
  Shield, 
  Users, 
  Globe, 
  Award, 
  ArrowRight,
  CheckCircle,
  Star,
  TrendingUp
} from '../components/icons';

const Mission = () => {
  const values = [
    {
      icon: Heart,
      title: 'Integrity',
      description: 'We do the right thing in procurement, partnerships, training, and client service, without compromise.'
    },
    {
      icon: Shield,
      title: 'Impact',
      description: 'We aim for meaningful change, improving clinical outcomes, empowering professionals, and strengthening systems.'
    },
    {
      icon: Users,
      title: 'Innovation',
      description: 'We pursue better solutions through modern products, smarter workflows, and continuous learning.'
    },
    {
      icon: Globe,
      title: 'Accessibility',
      description: 'We believe every clinic and patient deserves access to safe, modern, and affordable dental care.'
    }
  ];

  const achievements = [
    {
      number: '500+',
      label: 'Products Available',
      description: 'Comprehensive range of dental supplies and equipment'
    },
    {
      number: '50+',
      label: 'Clinics Served',
      description: 'Dental practices across East Africa'
    },
    {
      number: '5',
      label: 'Countries',
      description: 'Tanzania, Kenya, Uganda, Rwanda, Burundi'
    },
    {
      number: '98%',
      label: 'Satisfaction Rate',
      description: 'Customer satisfaction and trust'
    }
  ];

  const commitments = [
    'Integrated Training Model - We train professionals on product usage, creating real clinical value',
    'Mercury-Free Leadership - National and regional leader in mercury-free dentistry through Cention N program',
    'Public-Private Collaboration - Proven ability to align with MoH, MSD, and universities',
    'Clinical Support & Ethical Guidance - Ongoing product support backed by evidence and research',
    'Strategic Product Curation - Focused on innovation, affordability, and quality integration',
    'Rooted in Tanzania, Built for Growth - Local presence with long-term investment commitment'
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section data-hero className="relative pt-32 pb-20 overflow-hidden">
        {/* Background Image */}
        <div 
          className="absolute inset-0 bg-cover bg-center bg-no-repeat"
          style={{
            backgroundImage: "url('/images/our-mission-bg.jpg')"
          }}
        />
        {/* Overlay for better text readability */}
        <div className="absolute inset-0 bg-black/60" />
        
        <div className="container-custom relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center text-white"
          >
            <h1 className="text-4xl md:text-6xl font-bold mb-6 text-white">
              Our Mission
            </h1>
            <p className="text-xl md:text-2xl text-white/90 max-w-4xl mx-auto mb-8 leading-relaxed">
              To lead the transformation of dentistry through quality products, education, 
              and partnerships that elevate clinical outcomes and community health.
            </p>
            <p className="text-lg text-white/80 max-w-3xl mx-auto mb-8">
              "Advancing Dentistry, One Step at a Time."
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/contact"
                className="inline-flex items-center justify-center bg-white text-[#0D56A6] px-8 py-4 rounded-lg font-semibold hover:bg-gray-100 transition-colors duration-300"
              >
                Get in Touch
                <ArrowRight size={20} className="ml-2" />
              </Link>
              {CATALOGUE_ENABLED && (
                <Link
                  to="/catalogue"
                  className="inline-flex items-center justify-center border-2 border-white text-white px-8 py-4 rounded-lg font-semibold hover:bg-white hover:text-[#0D56A6] transition-colors duration-300"
                >
                  Explore Catalogue
                </Link>
              )}
            </div>
          </motion.div>
        </div>
      </section>

      {/* Vision Section */}
      <section className="py-20 bg-gray-50">
        <div className="container-custom">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
            >
              <div className="w-16 h-16 bg-[#0D56A6] rounded-2xl flex items-center justify-center mb-6">
                <Target size={32} className="text-white" />
              </div>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
                Our Vision
              </h2>
              <p className="text-lg text-gray-600 leading-relaxed mb-6">
                To transform how dentistry is taught, practised, and accessed in Tanzania. 
                Setting a standard for the region and beyond.
              </p>
              <p className="text-lg text-gray-600 leading-relaxed">
                We envision a future where every dental professional has access to modern, 
                safe, and affordable dental care through our integrated training model and 
                mercury-free leadership initiatives.
              </p>
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
              className="relative"
            >
              <div className="bg-white rounded-2xl shadow-xl p-8">
                <h3 className="text-2xl font-bold text-gray-900 mb-6 text-center">
                  Our Impact
                </h3>
                <div className="grid grid-cols-2 gap-6">
                  {achievements.map((achievement, index) => (
                    <div key={index} className="text-center">
                      <div className="text-3xl font-bold text-[#0D56A6] mb-2">
                        {achievement.number}
                      </div>
                      <div className="text-sm font-semibold text-gray-900 mb-1">
                        {achievement.label}
                      </div>
                      <div className="text-xs text-gray-600">
                        {achievement.description}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="py-20 bg-white">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Our Core Values
            </h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              These fundamental principles guide everything we do and shape our 
              commitment to excellence in dental care across East Africa.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {values.map((value, index) => (
              <motion.div
                key={value.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="text-center group"
              >
                <div className="w-20 h-20 bg-[#0D56A6]/10 rounded-2xl flex items-center justify-center mx-auto mb-6 group-hover:bg-[#0D56A6] transition-colors duration-300">
                  <value.icon size={40} className="text-[#0D56A6] group-hover:text-white transition-colors duration-300" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-4 group-hover:text-[#0D56A6] transition-colors duration-300">
                  {value.title}
                </h3>
                <p className="text-gray-600 leading-relaxed">
                  {value.description}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Commitments Section */}
      <section className="py-20 bg-gray-50">
        <div className="container-custom">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
            >
              <div className="w-16 h-16 bg-[#8EC641] rounded-2xl flex items-center justify-center mb-6">
                <Award size={32} className="text-white" />
              </div>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
                Our Commitments
              </h2>
              <p className="text-lg text-gray-600 leading-relaxed mb-8">
                We are dedicated to upholding the highest standards in everything we do, 
                from product quality to customer service and environmental responsibility.
              </p>
              <div className="space-y-4">
                {commitments.map((commitment, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, x: -20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.6, delay: index * 0.1 }}
                    className="flex items-start space-x-3"
                  >
                    <CheckCircle size={20} className="text-[#8EC641] mt-1 flex-shrink-0" />
                    <span className="text-gray-700">{commitment}</span>
                  </motion.div>
                ))}
              </div>
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
              className="relative"
            >
              <div className="bg-gradient-to-br from-[#0D56A6] to-[#8EC641] rounded-2xl p-8 text-white">
                <div className="flex items-center mb-6">
                  <Star size={32} className="text-yellow-300 mr-3" />
                  <h3 className="text-2xl font-bold">Why Choose Us?</h3>
                </div>
                <div className="space-y-4">
                  <div className="flex items-start space-x-3">
                    <TrendingUp size={20} className="text-yellow-300 mt-1 flex-shrink-0" />
                    <div>
                      <h4 className="font-semibold mb-1">Proven Track Record</h4>
                      <p className="text-white/90 text-sm">Years of experience serving dental professionals</p>
                    </div>
                  </div>
                  <div className="flex items-start space-x-3">
                    <Shield size={20} className="text-yellow-300 mt-1 flex-shrink-0" />
                    <div>
                      <h4 className="font-semibold mb-1">Quality Guaranteed</h4>
                      <p className="text-white/90 text-sm">All products meet international standards</p>
                    </div>
                  </div>
                  <div className="flex items-start space-x-3">
                    <Users size={20} className="text-yellow-300 mt-1 flex-shrink-0" />
                    <div>
                      <h4 className="font-semibold mb-1">Expert Support</h4>
                      <p className="text-white/90 text-sm">Dental professionals on our team</p>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-white">
        <div className="container-custom text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
              Join Us in Transforming Dentistry
            </h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto mb-8">
              Partner with Dentopharma to access our integrated training model, 
              mercury-free leadership initiatives, and strategic product curation 
              that elevates clinical outcomes and community health.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/contact"
                className="inline-flex items-center justify-center bg-[#0D56A6] text-white px-8 py-4 rounded-lg font-semibold hover:bg-[#0A4A8F] transition-colors duration-300"
              >
                Become a Partner
                <ArrowRight size={20} className="ml-2" />
              </Link>
              {CATALOGUE_ENABLED && (
                <Link
                  to="/catalogue"
                  className="inline-flex items-center justify-center border-2 border-[#0D56A6] text-[#0D56A6] px-8 py-4 rounded-lg font-semibold hover:bg-[#0D56A6] hover:text-white transition-colors duration-300"
                >
                  Explore Products
                </Link>
              )}
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default Mission;
